import {
  useEffect, useId, useRef, useState, FocusEvent, KeyboardEvent, CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';
import { style } from 'typestyle';
import { VideoSubtitleTrack, sortSubtitleTracksForDisplay } from './subtitles';

interface Props {
  tracks: VideoSubtitleTrack[];
  audioLang?: string;
  activeTrackIndex: number | null;
  onSelect: (index: number | null) => void;
}

export function SubtitleControl({ tracks, audioLang, activeTrackIndex, onSelect }: Props) {
  const [showMenu, setShowMenu] = useState(false);
  const controlRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({ visibility: 'hidden' });
  const multipleTracks = tracks.length > 1;
  const displayTracks = sortSubtitleTracksForDisplay(tracks, audioLang);
  const activeLabel = activeTrackIndex === null
    ? 'Off'
    : displayTracks.find((track) => track.sourceIndex === activeTrackIndex)?.label || `Track ${activeTrackIndex + 1}`;
  const buttonLabel = multipleTracks
    ? `Choose subtitles (${activeLabel})`
    : activeTrackIndex === null ? 'Enable subtitles' : 'Disable subtitles';

  useEffect(() => {
    setShowMenu(false);
  }, [tracks]);

  useEffect(() => {
    if (!showMenu) return undefined;
    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!controlRef.current?.contains(target) && !menuRef.current?.contains(target)) setShowMenu(false);
    };
    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [showMenu]);

  useEffect(() => {
    if (!showMenu || menuStyle.visibility !== 'visible') return;
    menuRef.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
  }, [menuStyle.visibility, showMenu]);

  useEffect(() => {
    if (!showMenu) {
      setMenuStyle({ visibility: 'hidden' });
      return undefined;
    }

    const updatePosition = () => {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      const menuRect = menuRef.current?.getBoundingClientRect();
      if (!buttonRect || !menuRect) return;
      const margin = 8;
      const gap = 8;
      const maxHeight = Math.max(0, Math.min(window.innerHeight * 0.6, window.innerHeight - margin * 2));
      const belowSpace = Math.max(0, window.innerHeight - buttonRect.bottom - gap - margin);
      const aboveSpace = Math.max(0, buttonRect.top - gap - margin);
      const preferAbove = belowSpace < Math.min(menuRect.height, 180) && aboveSpace > belowSpace;
      const availableHeight = Math.min(maxHeight, preferAbove ? aboveSpace : belowSpace);
      const menuHeight = Math.min(menuRect.height, availableHeight);
      const unclampedTop = preferAbove
        ? buttonRect.top - gap - menuHeight
        : buttonRect.bottom + gap;
      const maxTop = Math.max(margin, window.innerHeight - margin - menuHeight);
      const top = Math.min(Math.max(margin, unclampedTop), maxTop);
      const maxLeft = Math.max(margin, window.innerWidth - menuRect.width - margin);
      const left = Math.min(Math.max(margin, buttonRect.right - menuRect.width), maxLeft);
      setMenuStyle({ position: 'fixed', top, left, maxHeight: availableHeight, visibility: 'visible' });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [showMenu]);

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!showMenu) return;
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || (
      !controlRef.current?.contains(nextTarget) && !menuRef.current?.contains(nextTarget)
    )) setShowMenu(false);
  };

  const closeMenu = () => {
    setShowMenu(false);
    buttonRef.current?.focus();
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = menuRef.current
      ? Array.from(menuRef.current.querySelectorAll<HTMLButtonElement>('button')) : [];
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = items.length - 1;
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeMenu();
      return;
    } else return;
    event.preventDefault();
    items[nextIndex]?.focus();
  };

  const selectTrack = (index: number | null) => {
    onSelect(index);
    closeMenu();
  };

  const control = (
    <div ref={controlRef} className={styles.control} onBlur={handleBlur}>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.roundButton} ${activeTrackIndex !== null ? styles.active : ''}`}
        onClick={() => {
          if (multipleTracks) setShowMenu((visible) => !visible);
          else selectTrack(activeTrackIndex === null ? 0 : null);
        }}
        aria-label={buttonLabel}
        aria-pressed={activeTrackIndex !== null}
        aria-expanded={multipleTracks ? showMenu : undefined}
        aria-controls={multipleTracks ? menuId : undefined}
        title="Subtitles"
      >
        <SubtitlesIcon />
      </button>
    </div>
  );

  const menu = multipleTracks && showMenu ? createPortal(
        <div
          ref={menuRef}
          className={styles.menu}
          id={menuId}
          role="menu"
          aria-label="Subtitle language"
          style={menuStyle}
          onKeyDown={handleMenuKeyDown}
          onClick={(event) => event.stopPropagation()}
          onBlur={handleBlur}
        >
          <button
            type="button"
            role="menuitemradio"
            aria-checked={activeTrackIndex === null}
            className={`${styles.menuItem} ${activeTrackIndex === null ? styles.menuItemActive : ''}`}
            onClick={() => selectTrack(null)}
          >
            Off
          </button>
          {displayTracks.map((track) => (
            <button
              type="button"
              role="menuitemradio"
              aria-checked={activeTrackIndex === track.sourceIndex}
              className={`${styles.menuItem} ${activeTrackIndex === track.sourceIndex ? styles.menuItemActive : ''}`}
              key={`${track.srclang}-${track.sourceIndex}`}
              onClick={() => selectTrack(track.sourceIndex)}
            >
              {track.label}
            </button>
          ))}
        </div>,
        document.body,
      ) : null;

  return (
    <>
      {control}
      {menu}
    </>
  );
}

function SubtitlesIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="5" width="19" height="14" rx="2" stroke="white" strokeWidth="1.8" />
      <path
        d="M10 10.25c-.52-.52-1.22-.84-2-.84a2.84 2.84 0 1 0 0 5.68c.78 0 1.48-.32 2-.84M17 10.25c-.52-.52-1.22-.84-2-.84a2.84 2.84 0 1 0 0 5.68c.78 0 1.48-.32 2-.84"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

const styles = {
  control: style({ position: 'relative' }),
  roundButton: style({
    width: 40,
    height: 40,
    padding: 5,
    border: 'none',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(3px)',
    color: '#fff',
    cursor: 'pointer',
    outline: 'none',
    $nest: {
      '&:active': { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
      '&:focus-visible': { outline: '2px solid white', outlineOffset: '2px' },
    },
  }),
  active: style({ backgroundColor: 'rgba(0, 0, 0, 0.6)' }),
  menu: style({
    position: 'fixed',
    minWidth: 144,
    overflowY: 'auto',
    padding: 4,
    borderRadius: 8,
    background: 'rgba(20, 20, 20, 0.96)',
    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.35)',
    backdropFilter: 'blur(4px)',
    zIndex: 1500,
  }),
  menuItem: style({
    display: 'block',
    width: '100%',
    padding: '9px 12px',
    border: 'none',
    borderRadius: 5,
    background: 'transparent',
    color: '#fff',
    fontFamily: 'var(--adalong-subtitle-font-family, "Fustat", Arial, sans-serif)',
    fontSize: 13,
    lineHeight: 1.3,
    textAlign: 'left',
    cursor: 'pointer',
    $nest: {
      '&:hover': { background: 'rgba(255, 255, 255, 0.12)' },
      '&:focus-visible': { outline: '2px solid white', outlineOffset: '-2px' },
    },
  }),
  menuItemActive: style({ background: 'rgba(255, 255, 255, 0.18)', fontWeight: 700 }),
};
